import * as React from "react"

import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Icon } from "@/icons/icon"
import { cn, formatDate } from "@/lib/utils"
import { format } from "date-fns"

export interface DatePickerProps {
  className?: string
  label?: string
  placeholder?: string
  value?: string
  disabled?: boolean
  onChange?: (date: string) => void
}

export default function DatePicker({
  className,
  label = "",
  placeholder = "",
  value,
  disabled,
  onChange,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false)
  const [date, setDate] = React.useState<Date | undefined>(undefined)
  const id = React.useId()

  useSyncValue()

  function handleSelect(selected?: Date) {
    setDate(selected)
    setOpen(false)

    if (onChange) {
      onChange(selected ? format(selected, "yyyy-MM-dd") : "")
    }
  }

  function useSyncValue() {
    React.useEffect(() => {
      try {
        if (!value) {
          setDate(undefined)

          return
        }

        setDate(new Date(value))
      } catch {
        setDate(undefined)
      }
    }, [value])
  }

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {label && <Label htmlFor={id}>{label}</Label>}
      <Popover
        open={open}
        onOpenChange={(next) => setOpen(disabled ? false : next)}
      >
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            id={id}
            className="w-full max-w-48 justify-between items-center font-medium rounded-sm h-12 shadow-xs text-sm"
            disabled={disabled}
          >
            <div>{date ? formatDate(date) : placeholder}</div>
            <Icon name="Calendar" className="size-5 text-icon" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto overflow-hidden p-0" align="start">
          <Calendar
            className="text-sm"
            mode="single"
            selected={date}
            captionLayout="dropdown"
            onSelect={handleSelect}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}
